import { z } from 'zod';
/**
 * Velocity — hồ sơ tốc độ thực thi, spec mục 10 (APM-1001).
 *
 * ## Vì sao là MA TRẬN, không phải một con số
 *
 * Spec nói thẳng: "một người có thể nhanh ở backend nhưng chậm ở frontend".
 * Một con số tốc độ chung sẽ gộp hai điều đó lại và cho ra một trung bình
 * không mô tả ai — rồi mọi gợi ý tái phân bổ ở `APM-1002` dựa trên nó sẽ sai
 * một cách có hệ thống, không phải sai ngẫu nhiên.
 *
 * ## Vì sao mỗi ô mang cả căn cứ
 *
 * Đây là dữ liệu dùng để **đánh giá con người**. Spec mục 10 đòi "hiển thị rõ
 * căn cứ (dữ liệu lịch sử, độ tin cậy) cho cả PM và người bị ảnh hưởng, tránh
 * cảm giác bị AI đánh giá một cách mập mờ". Nên mọi ô đều nói ra số mẫu, độ
 * tin cậy, và số mẫu đã bị loại vì ngoại cảnh — và truy được về từng task.
 */
export const VelocityConfidence = z.enum(['low', 'medium', 'high']);
/**
 * Dưới ngưỡng này thì một phép đo không nên được đọc như một kết luận.
 *
 * ⚠️ Ở contract chứ không ở API vì **giao diện cũng phải biết nó** (APM-OKR-c):
 * trang lịch sử OKR gắn nhãn "ít mẫu" lên một kết cục *đạt* dựng trên hai việc.
 * Giữ hai hằng số 3 ở hai bên là để chúng trôi khỏi nhau, và lúc ấy bảng nói
 * "đủ tin" còn thẻ bên cạnh nói "ít mẫu" cho cùng một con số.
 */
export const MIN_SAMPLES = 3;
/** Trần mẫu đọc một lần. Ma trận của một tổ chức không cần hơn thế để ổn định. */
export const VELOCITY_SAMPLE_LIMIT = 5_000;
/** Cửa sổ mặc định: velocity của hai năm trước không nói gì về hôm nay. */
export const VELOCITY_WINDOW_DAYS = 365;
export const VelocityCell = z.object({
    userId: z.uuid(),
    /**
     * Tên người, trả kèm luôn.
     *
     * Không để client tự tra: danh bạ cấp tổ chức nằm sau `user:manage`, mà HR —
     * người dùng chính của màn hình này — không có quyền đó. Bắt client tra tên
     * nghĩa là bảng hiện ra một cột uuid, trên đúng màn hình mà cả điểm là **nói
     * về ai**. Cùng cách `APM-804` làm với bảng phân bổ.
     */
    userName: z.string(),
    taskTypeId: z.uuid(),
    taskTypeName: z.string(),
    /** `null` = mẫu không xác định được tech stack; xem `velocity_sample`. */
    techStackId: z.uuid().nullable(),
    techStackName: z.string().nullable(),
    /** Số mẫu **tính vào** tín hiệu năng lực (đã loại mẫu ngoại cảnh). */
    samples: z.number().int().nonnegative(),
    /**
     * Trung vị `worked/estimate`. 1.0 = đúng ước lượng, 1.5 = chậm 50%.
     *
     * Trung vị chứ không phải trung bình: một task bị kéo 5 lần so với ước lượng
     * sẽ đẩy trung bình của cả ô, và ô đó rồi được dùng để nói về một con người.
     */
    medianRatio: z.number().nonnegative(),
    /** Khoảng giữa (p25–p75) — thước đo "độ lệch có ổn định không". */
    p25: z.number().nonnegative(),
    p75: z.number().nonnegative(),
    confidence: VelocityConfidence,
    /**
     * Số mẫu bị loại vì **ngoại cảnh** (phần lớn thời gian là chờ, không phải
     * làm). Hiện ra chứ không giấu: một người có 6 task đều bị chặn thì vấn đề
     * là quy trình, và con số này là chỗ duy nhất nói được điều đó.
     */
    environmentSamples: z.number().int().nonnegative(),
    /** Tổng giờ chờ của các mẫu bị loại — quy mô của vấn đề ngoại cảnh. */
    environmentHours: z.number().nonnegative(),
    lastCompletedAt: z.iso.datetime(),
});
export const VelocityMatrix = z.object({
    from: z.iso.datetime(),
    to: z.iso.datetime(),
    cells: z.array(VelocityCell),
    /** Tổng mẫu trong cửa sổ, kể cả mẫu ngoại cảnh. */
    totalSamples: z.number().int().nonnegative(),
    /**
     * `true` khi đã đụng trần `VELOCITY_SAMPLE_LIMIT`.
     *
     * Nói ra thay vì im lặng cắt: một ma trận thiếu mẫu vẫn vẽ ra bảng, vẫn có
     * độ tin cậy, và không gì cho biết nó đang nói về một phần dữ liệu.
     */
    truncated: z.boolean(),
});
/** Một mẫu, cho drill-down: "vì sao ô này nói vậy". */
export const VelocitySampleView = z.object({
    taskId: z.uuid(),
    projectId: z.uuid(),
    taskTypeId: z.uuid(),
    techStackId: z.uuid().nullable(),
    estimateHours: z.number().nonnegative(),
    workedHours: z.number().nonnegative(),
    blockedHours: z.number().nonnegative(),
    /** `null` = không có giờ làm đo được (kéo thẳng sang xong) — APM-ACHEAT-c. */
    ratio: z.number().nonnegative().nullable(),
    environment: z.boolean(),
    completedAt: z.iso.datetime(),
});
// ── Gợi ý tái phân bổ (APM-1002, spec mục 10) ───────────────────────────────
export const RebalanceKind = z.enum(['reassign', 'reschedule']);
/**
 * Căn cứ của một đề xuất — **bắt buộc**, không phải tuỳ chọn.
 *
 * Spec mục 10: "khi có đề xuất tái phân bổ, nên hiển thị rõ căn cứ (dữ liệu
 * lịch sử, độ tin cậy) cho cả PM và người bị ảnh hưởng, tránh cảm giác bị AI
 * đánh giá một cách mập mờ — điều này ảnh hưởng trực tiếp đến việc team có
 * chấp nhận dùng tính năng hay không".
 *
 * Nên nó nằm trong chính kiểu dữ liệu: một đề xuất **không thể** tồn tại mà
 * thiếu căn cứ, và không màn hình nào "quên" hiện nó được.
 */
export const RebalanceEvidence = z.object({
    fromRatio: z.number().nonnegative(),
    fromSamples: z.number().int().nonnegative(),
    fromConfidence: VelocityConfidence,
    toRatio: z.number().nonnegative().nullable(),
    toSamples: z.number().int().nonnegative(),
    toConfidence: VelocityConfidence.nullable(),
    /** Số ngày quá tải trong cửa sổ đang xét. */
    fromOverloadedDays: z.number().int().nonnegative(),
    toOverloadedDays: z.number().int().nonnegative(),
});
export const RebalanceProposal = z.object({
    kind: RebalanceKind,
    taskId: z.uuid(),
    taskKey: z.string(),
    taskTitle: z.string(),
    taskTypeId: z.uuid(),
    fromUserId: z.uuid(),
    fromUserName: z.string(),
    /** `null` với `reschedule`: không đổi người, chỉ dời lịch. */
    toUserId: z.uuid().nullable(),
    toUserName: z.string().nullable(),
    /** Giờ ước tính tiết kiệm được. Là **ước tính**, nên luôn đứng cạnh căn cứ. */
    savedHours: z.number(),
    evidence: RebalanceEvidence,
});
export const RebalanceReport = z.object({
    from: z.iso.date(),
    to: z.iso.date(),
    proposals: z.array(RebalanceProposal),
    /**
     * Số task đã xét nhưng không sinh đề xuất nào.
     *
     * Hiện ra để "không có đề xuất" phân biệt được với "chưa chạy": im lặng ở
     * đây khiến người dùng nghĩ tính năng hỏng, rồi họ tắt nó.
     */
    considered: z.number().int().nonnegative(),
});
/** Nội dung một gợi ý đã lưu (`ai_suggestion.content` khi `skill = "rebalance"`). */
export const RebalanceSuggestionContent = z.object({
    proposals: z.array(RebalanceProposal).min(1),
    window: z.object({ from: z.iso.date(), to: z.iso.date() }),
});
/** Tên skill lưu ở `ai_suggestion.skill` — dùng chung FE↔BE để lọc. */
export const REBALANCE_SKILL = 'rebalance';
// ── Đánh giá hiệu suất (APM-1003, spec mục 14) ──────────────────────────────
/**
 * Một chiều của hồ sơ hiệu suất.
 *
 * `index` là 0–100 (cao hơn = tốt hơn) để bốn chiều vẽ được lên cùng một radar;
 * `raw` là con số thô đứng sau nó, để người đọc kiểm lại thay vì phải tin.
 * `null` = **chưa đo được**, khác hẳn 0 — trên màn hình đánh giá con người,
 * khác biệt đó là tất cả.
 */
export const PerformanceDimension = z.object({
    index: z.number().nullable(),
    raw: z.number().nullable(),
    samples: z.number().int().nonnegative(),
    confidence: VelocityConfidence,
});
/**
 * Hai chiều đến từ công việc **vận hành** — APM-ISSUE-i, docs/plan-issue-management.md §6.3.
 *
 * ## Vì sao cần, và vì sao chúng không phải `PerformanceDimension`
 *
 * Ở dự án bảo trì, 85–95% issue không đáng có task (kế hoạch §5). Nếu chỉ task
 * vào báo cáo hiệu suất thì công sức của đội vận hành **biến mất khỏi mọi bảng
 * đánh giá**: một người xử lý 120 ticket một tháng trông như không làm gì. Hai
 * con số này bịt đúng lỗ đó.
 *
 * Chúng không dùng `PerformanceDimension` vì không có baseline dự án nào để
 * chuẩn hoá theo: "đúng cam kết" là một tỉ lệ tuyệt đối, và giờ quy đổi là một
 * khối lượng. Ép chúng vào khuôn `index`/`raw` sẽ tạo ra một chỉ số chuẩn hoá
 * mà không ai giải thích được so với cái gì.
 *
 * ## ⚠️ `serviceHours` là GIỜ, không phải số ticket
 *
 * Đếm ticket thưởng cho việc nhặt cái dễ, chia nhỏ ticket, đóng sớm và né giúp
 * đồng đội — sai lầm được ghi chép kỹ nhất trong ngành về đo năng suất hỗ trợ.
 * Giờ quy đổi không thưởng cho việc chia nhỏ: chia một việc hai giờ thành bốn
 * việc vẫn là hai giờ.
 *
 * ## ⚠️ Và chúng vẫn không được cộng lại thành một điểm
 *
 * Bảy cái tên bị cấm ở `PerformanceRosterRow` áp nguyên vẹn cho hai chiều này.
 * `reopenCount` có mặt ở đây là **bắt buộc**, không phải tiện tay: mọi số tốc độ
 * phải đi kèm một đối trọng chất lượng, nếu không thì cách dễ nhất để đẹp số là
 * đóng ẩu.
 */
export const ServicePerformance = z.object({
    /** Tỉ lệ đúng cam kết xử lý, 0–1. `null` = không có mẫu nào **có** cam kết. */
    slaMetRatio: z.number().min(0).max(1).nullable(),
    /** Số vấn đề có cam kết đã đóng trong kỳ — mẫu số của tỉ lệ trên. */
    slaSamples: z.number().int().nonnegative(),
    /** Tổng **giờ quy đổi** (kế hoạch §6.2.1), làm tròn tới 0,1 giờ. */
    serviceHours: z.number().nonnegative(),
    /** Tổng vấn đề đã đóng trong kỳ, kể cả loại không cam kết. */
    closedCount: z.number().int().nonnegative(),
    /** Đối trọng chất lượng — bắt buộc đi kèm mọi số tốc độ. */
    reopenCount: z.number().int().nonnegative(),
});
export const PerformanceProject = z.object({
    projectId: z.uuid(),
    projectName: z.string(),
    workedHours: z.number().nonnegative(),
    /** Tỷ trọng thời gian — trọng số khi tổng hợp đa dự án (spec mục 14). */
    weight: z.number().min(0).max(1),
    /** Trung vị `ratio` của cả dự án: "PM này ước lượng rộng hay chặt". */
    projectBaseline: z.number().nonnegative(),
    normalisedRatio: z.number().nonnegative(),
    samples: z.number().int().nonnegative(),
});
/**
 * Hồ sơ hiệu suất một người — spec mục 14.
 *
 * ## ⚠️ Cố tình KHÔNG có điểm tổng
 *
 * Spec kết lại bằng: "là dữ liệu đầu vào cho **con người** quyết định, không
 * phải điểm số tự động cuối cùng". Một trường `score` sẽ được dùng như điểm
 * tổng — đem so, đem xếp hạng, đem vào bảng lương — bất kể ta viết bao nhiêu
 * chú thích quanh nó. Không có trường đó là cách duy nhất chắc chắn.
 */
/**
 * Cùng các chỉ số ấy, đo ở **kỳ liền trước** — APM-PERF2-g.
 *
 * ## Vì sao mang giá trị chứ không mang độ lệch
 *
 * Giao diện muốn nói "82% (kỳ trước 76%)" chứ không chỉ "↑6": một mũi tên
 * không có mốc thì người đọc không kiểm lại được, và đây là màn hình đánh giá
 * con người. Từ hai giá trị thì tính ra độ lệch lúc nào cũng được; từ độ lệch
 * thì không dựng lại được giá trị.
 *
 * ## ⚠️ Vì sao KHÔNG nhét `delta` vào `PerformanceDimension`
 *
 * `PerformanceDimension` đang được dùng ở bốn chỗ, và thêm một trường kỳ-trước
 * vào đó là bắt **mọi** chỗ gọi phải biết về kỳ trước — kể cả bảng nhóm, nơi
 * không hiển thị xu hướng và không muốn trả tiền cho truy vấn thứ hai.
 *
 * ## ⚠️ `null` ở cấp ngoài nghĩa là "chưa có kỳ trước để so"
 *
 * Khác hẳn một khối toàn 0. Một người mới vào làm tháng trước mà hiện "→ 0%"
 * là nói dối bằng một con số đúng: không có gì giảm cả, chỉ là chưa có gì để
 * so. `null` ở từng trường bên trong thì hẹp hơn: kỳ trước có dữ liệu, nhưng
 * riêng chiều ấy chưa đo được.
 */
export const PerformanceTrend = z.object({
    from: z.iso.datetime(),
    to: z.iso.datetime(),
    /** `index` của kỳ trước — cùng thang với `PerformanceDimension.index`. */
    speed: z.number().nullable(),
    onTime: z.number().nullable(),
    rework: z.number().nullable(),
    /** Tỉ lệ đúng cam kết của kỳ trước, 0–1. Cùng thang với `ServicePerformance`. */
    slaMetRatio: z.number().nullable(),
});
export const PerformanceProfile = z.object({
    userId: z.uuid(),
    userName: z.string(),
    from: z.iso.datetime(),
    to: z.iso.datetime(),
    speed: PerformanceDimension,
    onTime: PerformanceDimension,
    rework: PerformanceDimension,
    /**
     * Feedback review — luôn `null` cho tới khi có tích hợp review.
     *
     * Giữ trường lại (thay vì bỏ hẳn) vì spec mục 14 liệt kê nó, và một trục
     * trống trên radar **nói** rằng còn một tín hiệu chưa đo được. Bỏ hẳn thì
     * người đọc tưởng bốn tín hiệu của spec đã đủ ba.
     */
    review: PerformanceDimension,
    projects: z.array(PerformanceProject),
    totalSamples: z.number().int().nonnegative(),
    /** Mẫu bị loại vì ngoại cảnh — hiện ra, không giấu (xem `APM-1001`). */
    environmentSamples: z.number().int().nonnegative(),
    /**
     * Công việc vận hành — `null` khi người này **không** làm việc vận hành.
     *
     * `null` khác một khối toàn số 0: "không xử lý vấn đề nào" và "không làm việc
     * vận hành" dẫn tới hai cách đọc khác hẳn nhau, và chỉ cách thứ hai đúng với
     * một người chỉ làm dự án phát triển.
     */
    service: ServicePerformance.nullable(),
    /**
     * Kỳ liền trước, để so — `null` khi **chưa có kỳ trước nào có dữ liệu**.
     *
     * Chỉ những lối đọc về **một** người mới trả trường này (`/performance/me`,
     * `/performance/:userId`): nó tốn một lượt truy vấn thứ hai trên cả phạm vi,
     * và bảng nhóm không vẽ xu hướng nên không có lý do trả tiền cho nó.
     */
    previous: PerformanceTrend.nullable(),
});
/**
 * Tải **sắp tới** của chính người đang đăng nhập — APM-PERF2-h.
 *
 * ## ⚠️ Ô duy nhất nhìn về tương lai
 *
 * Mọi con số khác trên trang Hiệu suất kể lại quá khứ; cái này nói về tám tuần
 * tới. Đó là lý do nó là một khối riêng với nhãn thời gian của riêng nó, chứ
 * không đứng chung hàng với ba thẻ kia — gộp lại là mời người đọc hiểu "đang
 * nhận 132% sức" như một chuyện đã xảy ra trong quý vừa rồi.
 *
 * ## Vì sao không cần quyền và không ghi audit
 *
 * Đây là dữ liệu của **chính người gọi**. `CrossProjectAllocation` mang tên
 * người khác nên nó nhạy cảm và ghi `sensitive_access_log`; một người đọc lịch
 * của chính mình thì không.
 */
export const PersonalLoad = z.object({
    from: z.iso.datetime(),
    to: z.iso.datetime(),
    /** Số dự án đang có việc chưa xong. */
    projectCount: z.number().int().nonnegative(),
    committedHours: z.number().nonnegative(),
    capacityHours: z.number().nonnegative(),
    /** `committed / capacity × 100`. > 100 là đã hứa nhiều hơn số giờ có thật. */
    allocationPct: z.number().nonnegative(),
});
/** Hồ sơ của nhiều người — view theo dự án của PM. */
export const PerformanceReport = z.object({
    from: z.iso.datetime(),
    to: z.iso.datetime(),
    profiles: z.array(PerformanceProfile),
});
/**
 * Cửa sổ mặc định của **bảng nhân sự**, ngắn hơn hẳn `VELOCITY_WINDOW_DAYS`.
 *
 * Hai lý do cùng chiều. Câu người quản lý thật sự hỏi khi mở bảng là "quý vừa
 * rồi thế nào", không phải "một năm qua". Và nó cắt lượng mẫu khoảng bốn lần,
 * nên trần 5.000 mẫu gần như không chạm tới — cách rẻ nhất để `truncated` là
 * một cảnh báo hiếm chứ không phải trạng thái thường trực.
 *
 * Hồ sơ **một người** giữ nguyên 365 ngày: ở đó người đọc đang đánh giá một
 * con người cụ thể và cần cả năm, không phải một quý.
 */
export const PERFORMANCE_ROSTER_WINDOW_DAYS = 90;
/**
 * Một hàng của bảng hiệu suất nhân sự — APM-PERF-a.
 *
 * ## ⚠️ Không có điểm tổng, dưới BẤT KỲ tên gọi nào
 *
 * `PerformanceProfile` đã nói vì sao không có `score`. Ở bảng thì áp lực đặt
 * thêm một cột "tổng" mạnh hơn nhiều, vì bảng vốn để so sánh — nên nói thẳng
 * cả họ tên của nó: không `score`, `overall`, `rating`, và cũng không `rank`,
 * `percentile`, `band`, `grade`. Đó là cùng một thứ mặc bảy cái áo.
 *
 * Hệ quả kèm theo, dễ quên hơn: **thứ tự mặc định là theo tên**, không theo bất
 * kỳ chiều hiệu suất nào. Một bảng mở ra đã xếp sẵn theo "Tốc độ giảm dần" *là*
 * một bảng xếp hạng, dù schema sạch bong.
 *
 * ## Vì sao không tái dùng `PerformanceProfile`
 *
 * Nó mang `projects: PerformanceProject[]` — với 500 người thì đó là một payload
 * lớn cho thứ không hiện trên bảng. Chi tiết theo dự án đã có chỗ của nó: bấm
 * một hàng rồi gọi `GET /performance/:userId`. Việc đó ghi một dòng audit riêng,
 * và đúng như vậy — mở hồ sơ đầy đủ của một người là một lần đọc sâu hơn, sổ
 * audit phải phân biệt được hai mức.
 */
export const PerformanceRosterRow = z.object({
    userId: z.uuid(),
    userName: z.string(),
    /** `null` = chưa gán phòng ban. Nhãn để lọc, **không** phải tầng phân quyền. */
    departmentName: z.string().nullable(),
    // ── Hiệu suất trong kỳ đánh giá (`from`–`to`, nhìn về quá khứ) ────────────
    speed: PerformanceDimension,
    onTime: PerformanceDimension,
    rework: PerformanceDimension,
    /** Luôn `null` cho tới khi có tích hợp review — xem `PerformanceProfile`. */
    review: PerformanceDimension,
    totalSamples: z.number().int().nonnegative(),
    /** Mẫu bị loại vì ngoại cảnh. Cột đối trọng bắt buộc, xem `APM-1001`. */
    environmentSamples: z.number().int().nonnegative(),
    /**
     * Số dự án và số giờ **trong kỳ**.
     *
     * Miễn phí (đã nằm trong profile) và là trọng số đứng sau mọi chỉ số còn lại:
     * có chúng thì "chậm" của người 4 giờ/kỳ không đọc như "chậm" của người 400
     * giờ/kỳ.
     */
    windowProjectCount: z.number().int().nonnegative(),
    windowHours: z.number().nonnegative(),
    /**
     * Công việc vận hành trong kỳ — `null` khi người này không làm việc vận hành.
     *
     * Xem `ServicePerformance`: ở dự án bảo trì thì phần lớn công sức không đi
     * qua task, nên không có cột này thì người xử lý 120 vấn đề một tháng hiện ra
     * là một hàng trống.
     */
    service: ServicePerformance.nullable(),
    // ── Tải hiện tại (`capacityFrom`–`capacityTo`, nhìn về TƯƠNG LAI) ─────────
    committedHours: z.number().nonnegative(),
    capacityHours: z.number().nonnegative(),
    allocationPct: z.number().nonnegative(),
    activeProjectCount: z.number().int().nonnegative(),
});
/**
 * Bảng hiệu suất nhân sự — Director xem cả tổ chức, quản lý trực tiếp xem cấp
 * dưới của mình.
 *
 * ## Hai cặp mốc thời gian, không phải một
 *
 * Nhóm cột hiệu suất nhìn **quá khứ** (`from`–`to`), nhóm cột tải nhìn **tương
 * lai** (`capacityFrom`–`capacityTo`). Gộp thành một cặp là mời người đọc hiểu
 * "phân bổ 120%" như một chuyện đã xảy ra trong quý vừa rồi, trong khi nó nói
 * về tám tuần tới.
 */
export const PerformanceRoster = z.object({
    from: z.iso.datetime(),
    to: z.iso.datetime(),
    capacityFrom: z.iso.datetime(),
    capacityTo: z.iso.datetime(),
    /**
     * Kỳ này chạm trần mẫu, nên số liệu **đầu kỳ đã bị cắt**.
     *
     * Không có cờ này thì việc cắt diễn ra âm thầm: người chỉ có sản lượng ở đầu
     * kỳ hiện ra là "chưa đủ dữ liệu", và sai số đó rơi đúng trên màn hình dùng
     * để đánh giá con người. Giao diện phải nói ra, không được nuốt.
     */
    truncated: z.boolean(),
    rows: z.array(PerformanceRosterRow),
});
//# sourceMappingURL=velocity.js.map